import type { ReactNode } from 'react';

// Small Markdown renderer for LLM output: headings, paragraphs, nested lists, tables, quotes, rules, fenced code,
// bold / italic / inline code, links and search citations.
// Everything is rendered as React text nodes — no HTML from the model ever reaches the DOM, and only http(s) / mailto links become anchors.

const LINK = String.raw`\[(?:[^\[\]]|\[[^\[\]]*\])+\]\(\s*[^\s()]+(?:\([^\s()]*\)[^\s()]*)*(?:\s+"[^"]*")?\s*\)`;
const LINK_PARTS = /^\[((?:[^[\]]|\[[^[\]]*\])+)\]\(\s*([^\s()]+(?:\([^\s()]*\)[^\s()]*)*)(?:\s+"[^"]*")?\s*\)$/;
// Search-backed models cite as "([site](url))", sometimes several per bracket; those render as compact source chips.
const CITE = String.raw`[(（]\s*${LINK}(?:\s*[,;，；、]?\s*${LINK})*\s*[)）]`;
const TOKEN = new RegExp(
  [
    '`[^`\\n]+`',
    CITE,
    LINK,
    String.raw`\*\*\*\S(?:.*?\S)??\*\*\*`,
    String.raw`\*\*\S(?:.*?\S)??\*\*`,
    String.raw`\*[^\s*](?:[^*]*?[^\s*])??\*`,
    String.raw`https?:\/\/[^\s<>()\u0080-\uffff]+`,
  ].join('|'),
  'g',
);

function safeHref(url: string): string | null {
  return /^(https?:\/\/|mailto:)/i.test(url) ? url : null;
}

function anchor(href: string, children: ReactNode, key: number, className?: string): ReactNode {
  return <a key={key} href={href} className={className} target="_blank" rel="noopener noreferrer">{children}</a>;
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(TOKEN)) {
    let tok = m[0];
    if (m.index < last) continue;
    if (m.index > last) out.push(text.slice(last, m.index));
    if (tok.startsWith('`')) out.push(<code key={key++}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('***')) out.push(<strong key={key++}><em>{inline(tok.slice(3, -3))}</em></strong>);
    else if (tok.startsWith('**')) out.push(<strong key={key++}>{inline(tok.slice(2, -2))}</strong>);
    else if (tok.startsWith('*')) out.push(<em key={key++}>{inline(tok.slice(1, -1))}</em>);
    else if (tok.startsWith('[')) {
      const [, label, url] = LINK_PARTS.exec(tok)!;
      const href = safeHref(url);
      out.push(href ? anchor(href, inline(label), key++) : label);
    } else if (tok.startsWith('http')) {
      tok = tok.replace(/[.,;:!?'"\]]+$/, '');
      out.push(anchor(tok, tok, key++));
    } else {
      const links = [...tok.matchAll(new RegExp(LINK, 'g'))].map((l) => LINK_PARTS.exec(l[0])!);
      if (links.every((l) => safeHref(l[2]))) {
        out.push(<span key={key++} className="md-cites">{links.map((l, n) => anchor(l[2], l[1], n, 'md-cite'))}</span>);
      } else out.push(tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Inline content where the model's own line breaks are kept (it uses them deliberately, e.g. a dateline above a summary). */
function inlineLines(lines: string[]): ReactNode[] {
  return lines.flatMap((l, n) => (n === 0 ? inline(l) : [<br key={`br${n}`} />, ...inline(l)]));
}

const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const FENCE = /^\s{0,3}(```|~~~)/;
const QUOTE = /^\s{0,3}>\s?/;
const LIST = /^(\s*)([-*+•]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_SEP = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const NUMERIC = /^(约|~|≈)?\s*[+\-−–]?\s*[$¥€£]?\s*\d[\d,.]*\s*\S{0,5}$/;

const width = (indent: string) => indent.replace(/\t/g, '    ').length;
const cells = (row: string) => row.replace(/\\\|/g, '\u0000').trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim().replace(/\u0000/g, '|'));
const isTableStart = (lines: string[], i: number) => lines[i].includes('|') && i + 1 < lines.length && lines[i + 1].includes('-') && lines[i + 1].includes('|') && TABLE_SEP.test(lines[i + 1]);
const startsBlock = (lines: string[], i: number) => HEADING.test(lines[i]) || RULE.test(lines[i]) || FENCE.test(lines[i]) || QUOTE.test(lines[i]) || LIST.test(lines[i]) || isTableStart(lines, i);

function parseList(lines: string[], start: number, key: number): [ReactNode, number] {
  const first = LIST.exec(lines[start])!;
  const indent = width(first[1]);
  const ordered = /\d/.test(first[2]);
  const items: ReactNode[] = [];
  let i = start;
  while (i < lines.length) {
    const m = LIST.exec(lines[i]);
    if (!m || width(m[1]) < indent || /\d/.test(m[2]) !== ordered) break;
    const body = [m[3]];
    const children: ReactNode[] = [];
    i++;
    while (i < lines.length) {
      if (!lines[i].trim()) {
        // A blank line only stays inside the list when another item follows it.
        let j = i + 1;
        while (j < lines.length && !lines[j].trim()) j++;
        const next = j < lines.length ? LIST.exec(lines[j]) : null;
        if (!next || width(next[1]) < indent) break;
        i = j;
        continue;
      }
      const n = LIST.exec(lines[i]);
      if (n) {
        if (width(n[1]) <= indent) break;
        const [child, after] = parseList(lines, i, children.length);
        children.push(child);
        i = after;
      } else if (/^\s/.test(lines[i]) && children.length === 0 && !startsBlock(lines, i)) body.push(lines[i++].trim());
      else break;
    }
    items.push(<li key={items.length}>{inlineLines(body)}{children}</li>);
  }
  return [ordered ? <ol key={key} start={parseInt(first[2], 10)}>{items}</ol> : <ul key={key}>{items}</ul>, i];
}

function parseTable(lines: string[], start: number, key: number): [ReactNode, number] {
  const head = cells(lines[start]);
  const marks = cells(lines[start + 1]);
  const rows: string[][] = [];
  let i = start + 2;
  while (i < lines.length && lines[i].trim() && lines[i].includes('|')) rows.push(cells(lines[i++]));
  // Columns the model left unaligned are right-aligned when every cell is a figure, like the rest of the ledger.
  const align = head.map((_, c) => {
    const mark = marks[c] ?? '';
    if (mark.endsWith(':')) return mark.startsWith(':') ? 'center' : 'right';
    if (mark.startsWith(':')) return 'left';
    const filled = rows.map((r) => r[c] ?? '').filter(Boolean);
    return filled.length > 0 && filled.every((v) => NUMERIC.test(v)) ? 'right' : 'left';
  });
  const cls = (c: number) => (align[c] === 'right' ? 'num' : undefined);
  return [
    <div key={key} className="md-table">
      <table>
        <thead><tr>{head.map((h, c) => <th key={c} className={cls(c)} style={{ textAlign: align[c] }}>{inline(h)}</th>)}</tr></thead>
        <tbody>{rows.map((r, n) => <tr key={n}>{head.map((_, c) => <td key={c} className={cls(c)} style={{ textAlign: align[c] }}>{inline(r[c] ?? '')}</td>)}</tr>)}</tbody>
      </table>
    </div>,
    i,
  ];
}

function parseBlocks(lines: string[]): ReactNode[] {
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith(fence[1])) code.push(lines[i++]);
      i++;
      blocks.push(<pre key={key++}><code>{code.join('\n')}</code></pre>);
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      const Tag = `h${h[1].length}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(<Tag key={key++}>{inline(h[2])}</Tag>);
      i++;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push(<hr key={key++} />);
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) quoted.push(lines[i++].replace(QUOTE, ''));
      blocks.push(<blockquote key={key++}>{parseBlocks(quoted)}</blockquote>);
      continue;
    }
    if (isTableStart(lines, i)) {
      const [table, after] = parseTable(lines, i, key++);
      blocks.push(table);
      i = after;
      continue;
    }
    if (LIST.test(line)) {
      const [list, after] = parseList(lines, i, key++);
      blocks.push(list);
      i = after;
      continue;
    }
    const para = [line.trim()];
    i++;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines, i)) para.push(lines[i++].trim());
    blocks.push(<p key={key++}>{inlineLines(para)}</p>);
  }
  return blocks;
}

export function Markdown({ text }: { text: string }) {
  // Model output is arbitrary; a parser slip must degrade to the raw text, never blank the page.
  try {
    return <div className="md">{parseBlocks(text.replace(/\r\n?/g, '\n').split('\n'))}</div>;
  } catch {
    return <pre className="md" style={{ whiteSpace: 'pre-wrap', font: 'inherit' }}>{text}</pre>;
  }
}
